/**
 * Installability and updates.
 *
 * Registering the worker is three lines; the rest of this file is about being
 * honest with the person when a new version arrives. A health app that silently
 * swaps its measurement engine underneath someone mid-reading is not something
 * to be relaxed about, so the update is prepared quietly and applied only when
 * they say so.
 */

let deferredPrompt = null;
const listeners = new Set();

function emit(state) {
  for (const fn of listeners) fn(state);
}

export function subscribePwa(fn) {
  listeners.add(fn);
  fn(current);
  return () => listeners.delete(fn);
}

let current = { installable: false, installed: false, updateReady: false };

function update(patch) {
  current = { ...current, ...patch };
  emit(current);
}

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome would otherwise show its own bar at a moment of its choosing.
    event.preventDefault();
    deferredPrompt = event;
    update({ installable: true });
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    update({ installable: false, installed: true });
  });

  if (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone) {
    update({ installed: true });
  }

  if (!('serviceWorker' in navigator)) return;
  // A service worker needs a secure context; in dev over plain http it simply
  // will not register, which is fine and not worth an error.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install; that is not an
            // update, it is the app becoming offline-capable for the first time.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              update({ updateReady: true });
            }
          });
        });
      })
      .catch(() => {
        /* offline capability is a bonus, never a requirement */
      });
  });
}

/**
 * Show the browser's own install prompt. Must be called from a user gesture.
 *
 * Throws nothing: this is wired straight to a click handler, and prompt()
 * raises InvalidStateError if the event was already consumed. Unhandled, that
 * left the button doing visibly nothing at all.
 */
export async function promptInstall() {
  if (!deferredPrompt) return false;
  try {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    update({ installable: false });
    return outcome === 'accepted';
  } catch {
    // The prompt is spent. Hiding the control is the honest response — leaving
    // it on screen invites the person to keep pressing a button that cannot work.
    deferredPrompt = null;
    update({ installable: false });
    return false;
  }
}

/**
 * Apply a waiting update, then reload once the new worker takes over.
 *
 * getRegistration() rejects when storage access is denied, and this is a click
 * handler, so the rejection went nowhere: the notice stayed on screen and the
 * button did nothing however many times it was pressed. A reload is the right
 * fallback — it is what the button promises, and the new worker will be picked
 * up on the way back in.
 */
export async function applyUpdate() {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.waiting) {
      window.location.reload();
      return;
    }
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    registration.waiting.postMessage('skip-waiting');
  } catch {
    window.location.reload();
  }
}
